import {
  IconStar,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react'
import { useRef } from 'react'
import TrustpilotBadge from './TrustpilotBadge'
import CTAButton from './shared/CTAButton'

const reviews = [
  {
    id: 1,
    rating: 5,
    title: 'Easy to use',
    content: 'Easy to use 👍. Thanks for your help & services.',
    author: 'Christakis',
  },
  {
    id: 2,
    rating: 5,
    title: 'Easy to understand',
    content: 'This was easy to understand, read and fill in.',
    author: 'Patricia Newman',
  },
  {
    id: 3,
    rating: 5,
    title: 'Great easy claim form',
    content: 'Great easy claim form.',
    author: 'Funluvindiva',
  },
  {
    id: 4,
    rating: 5,
    title: 'Quick and easy',
    content: 'Quick and easy to fill out.',
    author: 'Lorraine Welsh',
  },
  {
    id: 5,
    rating: 5,
    title: 'Easy to do',
    content: 'Easy to do did on behalf of an elderly friend.',
    author: 'Malcolm Duncan',
  },
  {
    id: 6,
    rating: 4,
    title: 'Very easy',
    content: 'Very easy but only time will tell if effective.',
    author: 'Mark McKenny',
  },
  {
    id: 7,
    rating: 4,
    title: 'So far so good',
    content:
      "So far so good if Claims1 actually finds all my past car finance and get me some compensation I'll be happy watch this space.",
    author: 'Audrey Smith',
  },
  {
    id: 8,
    rating: 5,
    title: 'Brilliant customer service',
    content:
      'Customer service was brilliant - answered all my questions. Simple process.',
    author: 'Hristiyan Pashkov',
  },
  {
    id: 9,
    rating: 4,
    title: 'Happy with claim management',
    content:
      'Happy with how my claim was managed. Clear communication and fast updates.',
    author: 'Phuong Hang',
  },
  {
    id: 10,
    rating: 5,
    title: 'Smooth experience',
    content:
      'Smooth experience from start to finish. Easy form and helpful team.',
    author: 'Crew Trip',
  },
]

function StarRating({ rating }) {
  return (
    <div className="mb-4">
      <div className="flex gap-0.5">
        {rating === 5 ? (
          <>
            {[...Array(5)].map((_, i) => (
              <IconStar
                key={i}
                className="h-5 w-5 fill-orange-400 text-orange-400"
              />
            ))}
          </>
        ) : rating === 4 ? (
          <>
            {[...Array(4)].map((_, i) => (
              <IconStar
                key={i}
                className="h-5 w-5 fill-orange-400 text-orange-400"
              />
            ))}
            <IconStar className="h-5 w-5 fill-gray-300 text-gray-300" />
          </>
        ) : (
          <>
            {[...Array(5)].map((_, i) => (
              <IconStar
                key={i}
                className={`h-5 w-5 ${
                  i < rating
                    ? 'fill-orange-400 text-orange-400'
                    : 'fill-gray-300 text-gray-300'
                }`}
              />
            ))}
          </>
        )}
      </div>
    </div>
  )
}

function ReviewCard({ review }) {
  return (
    <div className="flex h-full w-full min-w-0 flex-col overflow-hidden rounded-lg bg-white p-4">
      <div className="pt-6">
        <StarRating rating={review.rating} />
      </div>
      <p
        className="overflow-wrap-anywhere mb-4 flex-1 text-sm leading-relaxed break-words"
        style={{ color: 'var(--foreground)' }}
      >
        {review.content}
      </p>
      <div className="text-xs">
        <div
          className="overflow-wrap-anywhere mb-1 font-semibold break-words uppercase"
          style={{ color: 'var(--foreground)' }}
        >
          {review.author}
        </div>
      </div>
    </div>
  )
}

function StatisticsCard() {
  return (
    <div className="h-full w-full min-w-0">
      <div
        className="h-full overflow-hidden rounded-3xl border-0"
        style={{
          background:
            'linear-gradient(to bottom right, oklch(0.2205 0.0376 194.77) 0%, oklch(0.6942 0.1185 194.77) 70%, oklch(0.769 0.188 70.08) 100%)',
        }}
      >
        <div className="relative flex h-full flex-col justify-between p-6 text-white">
          <div className="relative z-10 flex flex-1 flex-col justify-center">
            <div className="mb-6">
              <h3 className="text-lg leading-tight font-bold break-words sm:text-xl">
                We&apos;ve helped{' '}
                <span className="text-amber-500">hundreds</span> of customers
                get paid compensation
              </h3>
            </div>

            <div className="text-center">
              <div className="flex items-center justify-center gap-2">
                <div className="text-lg">⭐</div>
                <div className="text-3xl font-black sm:text-4xl">Excellent</div>
                <div className="text-lg">⭐</div>
              </div>
              <div className="mt-2 text-sm font-medium">
                Rated 4.5 on Google
              </div>
            </div>
          </div>

          <div className="relative z-10 mt-4">
            <div className="rounded-lg bg-black/20 px-3 py-2 text-center text-xs sm:text-sm">
              <span className="font-medium break-words">
                Rated excellent on Google by our clients.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function Reviews({ className = '' }) {
  const scrollContainerRef = useRef(null)

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const cardWidth =
        scrollContainerRef.current.querySelector('[data-card]').offsetWidth
      const gap = 16 // Account for gap between cards
      scrollContainerRef.current.scrollBy({
        left: -(cardWidth + gap),
        behavior: 'smooth',
      })
    }
  }

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const cardWidth =
        scrollContainerRef.current.querySelector('[data-card]').offsetWidth
      const gap = 16 // Account for gap between cards
      scrollContainerRef.current.scrollBy({
        left: cardWidth + gap,
        behavior: 'smooth',
      })
    }
  }

  return (
    <section className={`w-full bg-gray-100 pt-8 ${className}`}>
      <div className="mx-auto max-w-7xl px-4">
        <div className="mb-8 text-center">
          <h2
            className="text-4xl font-black sm:text-5xl"
            style={{ color: 'var(--foreground)' }}
          >
            OUR CUSTOMERS LOVE US
          </h2>
        </div>

        <div className="mb-12 text-center">
          <div
            className="font-raleway-numbers mb-4 text-lg font-medium sm:text-xl"
            style={{ color: 'var(--foreground)' }}
          >
            Rated excellent by our customers, supported by glowing 4 & 5 star
            reviews on Google.
          </div>
          <div className="flex justify-center">
            <TrustpilotBadge />
          </div>
        </div>

        <div className="relative">
          <div className="flex items-center justify-between">
            <button
              onClick={scrollLeft}
              className="z-10 flex-shrink-0 rounded-full border-2 border-gray-200 bg-white p-3 shadow-lg transition-all duration-300 hover:border-teal-300 hover:bg-gray-50 hover:shadow-xl focus:ring-2 focus:ring-teal-300 focus:outline-none"
              aria-label="Scroll left"
            >
              <IconChevronLeft className="h-6 w-6 text-gray-700" />
            </button>

            <div
              ref={scrollContainerRef}
              className="scrollbar-hide mx-4 flex flex-1 gap-4 overflow-x-auto py-4"
              style={{ scrollSnapType: 'x mandatory' }}
            >
              <div
                data-card
                className="h-80 w-[calc(100%-2rem)] flex-shrink-0 sm:w-[calc(100%-2rem)] md:w-80"
                style={{ scrollSnapAlign: 'start' }}
              >
                <StatisticsCard />
              </div>
              {reviews.map(review => (
                <div
                  key={review.id}
                  data-card
                  className="h-80 w-[calc(100%-2rem)] flex-shrink-0 sm:w-[calc(100%-2rem)] md:w-80"
                  style={{ scrollSnapAlign: 'start' }}
                >
                  <ReviewCard review={review} />
                </div>
              ))}
            </div>

            <button
              onClick={scrollRight}
              className="z-10 flex-shrink-0 rounded-full border-2 border-gray-200 bg-white p-3 shadow-lg transition-all duration-300 hover:border-teal-300 hover:bg-gray-50 hover:shadow-xl focus:ring-2 focus:ring-teal-300 focus:outline-none"
              aria-label="Scroll right"
            >
              <IconChevronRight className="h-6 w-6 text-gray-700" />
            </button>
          </div>
        </div>

        {/* CTA Section */}
        <div className="pt-6 pb-6">
          <div className="container mx-auto px-4 text-center">
            <h2 className="mb-6 text-3xl font-black text-[#002020] sm:text-4xl">
              Ready to Start Your Claim?
            </h2>
            <p className="mx-auto mb-8 max-w-2xl text-lg text-[#002020]">
              Let our experienced team help you recover the money you may be
              owed from your car finance agreement.
            </p>
            <div className="mb-4">
              <CTAButton />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Reviews
